import type {Middleware} from '@reduxjs/toolkit';
import type {RootState} from '@/store';
import {openPanelInModal, closeModal, closeAllModals} from './modal-container-reducer';
import {hidePanel, unhidePanel} from '@Components/panel/panel-reducer';

const hasAnyOpenModal = (state: RootState): boolean => {
  const modalsHashmap = state.modals.modalsHashmap;
  const panelHashmap = state.panels.panelHashmap;
  for (const [, modalData] of Object.entries(modalsHashmap)) {
    if (modalData.isOpen) {
      const panelId = (modalData.panelProps?.panelId ?? modalData.panelProps?.id) as string;
      if (panelId && modalData.panelProps.hideOnClose) {
        if (!panelHashmap[panelId].isHidden) {
          return true
        }
        continue;
      }
      return true;
    }
  }
  return false;
};

const hasModalHistoryEntry = (): boolean => {
  return !!history.state?.pmwModal;
};

const getState = (): RootState => {
  return window.PMW.redux.store.getState();
};

export const modalHistoryMiddleware: Middleware = (store) => {
  let pendingRemoval = false;

  const removeModalHistoryEntry = (): void => {
    if (hasModalHistoryEntry()) {
      history.back();
    }
  };

  const scheduleRemoval = (): void => {
    pendingRemoval = true;
    setTimeout((): void => {
      if (!pendingRemoval) {
        return;
      }
      pendingRemoval = false;
      if (hasModalHistoryEntry() && !hasAnyOpenModal(getState())) {
        removeModalHistoryEntry();
      }
    }, 100);
  };

  window.addEventListener('popstate', (): void => {
    if (hasAnyOpenModal(getState())) {
      const {modalsHashmap} = getState().modals;
      for (const [modalId, modalData] of Object.entries(modalsHashmap)) {
        if (modalData.isOpen) {
          if (modalData.panelProps?.hideOnClose) {
            const panelId = (modalData.panelProps?.panelId ?? modalData.panelProps?.id) as string;
            if (panelId) {
              store.dispatch(hidePanel(panelId));
            }
          } else {
            store.dispatch(closeModal(modalId));
          }
        }
      }
    } else if (hasModalHistoryEntry()) {
      history.back();
    }
  });

  return (next) => (action) => {
    const result = next(action);
    if (openPanelInModal.match(action) || unhidePanel.match(action)) {
      if (pendingRemoval) {
        pendingRemoval = false;
      } else if (!hasModalHistoryEntry()) {
        history.pushState({pmwModal: true}, '');
      }
    }
    if ((closeModal.match(action) || hidePanel.match(action) || closeAllModals.match(action)) && hasModalHistoryEntry()) {
      scheduleRemoval();
    }

    return result;
  };
};
